import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";

import { RobotoTypography } from "@/shared/components";
import { DataSourceSelector } from "@/shared/components/visualization/controls/DataSourceSelector";
import { MessagePathNode, PathParser } from "@/shared/domain/topics";
import { actions, useVizDispatch } from "@/shared/state/visualization";
import { TopicData } from "@/shared/state/visualization/schema/v1";

import { isViewableAsConsoleLog } from "../../Panel/predicates";

import styles from "./Columns.module.css";
import { useIsDuplicateColumn } from "./useIsDuplicateColumn";

interface ColumnsProps {
  panelId: string;
  columns: TopicData[];
}

export function Columns({ panelId, columns }: ColumnsProps) {
  const isExistingColumn = useIsDuplicateColumn(columns);

  if (columns.length === 0) {
    return null;
  }

  return (
    <div className={styles.columns}>
      {columns.map((column, i) => {
        return (
          <Column
            key={column.messagePath.id}
            column={column}
            panelId={panelId}
            columnName={`Column ${i + 1}`}
            isExistingColumn={isExistingColumn}
          />
        );
      })}
    </div>
  );
}

interface ColumnProps {
  panelId: string;
  column: TopicData;
  columnName: string;
  isExistingColumn: (nextTopicData: TopicData) => boolean;
}

function Column({
  panelId,
  column,
  columnName,
  isExistingColumn,
}: ColumnProps) {
  return (
    <div className={styles.column}>
      <div className={styles.columnHeader}>
        <RobotoTypography variant="overline">{columnName}</RobotoTypography>
        <ColumnControls panelId={panelId} column={column} />
      </div>
      <ColumnData
        panelId={panelId}
        topicData={column}
        isExistingColumn={isExistingColumn}
      />
    </div>
  );
}

interface ColumnControlsProps {
  panelId: string;
  column: TopicData;
}

function ColumnControls({ panelId, column }: ColumnControlsProps) {
  const dispatch = useVizDispatch();

  const onRemoveColumn = (column: TopicData) => {
    dispatch(
      actions.removeMessagePathFromLogPanel(panelId, column.messagePath.id),
    );
  };

  return (
    <div>
      <IconButton
        aria-label="remove"
        className={styles.iconButton}
        onClick={() => onRemoveColumn(column)}
        title="Remove Column"
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
    </div>
  );
}

interface ColumnDataProps {
  panelId: string;
  topicData: TopicData;
  isExistingColumn: (nextTopicData: TopicData) => boolean;
}

function ColumnData({ panelId, topicData, isExistingColumn }: ColumnDataProps) {
  const dispatch = useVizDispatch();
  const column = {
    fileId: topicData.topic.association.association_id,
    path: [
      topicData.topic.name,
      MessagePathNode.partsToString(topicData.messagePath.parts),
    ].join(PathParser.TOPIC_SEPARATOR),
  };

  return (
    <div>
      <RobotoTypography component={"label"} variant="subtitle2">
        Message Path
      </RobotoTypography>
      <DataSourceSelector
        initialFile={column.fileId}
        initialPath={column.path}
        onChange={(messagePathNode) => {
          // Update the column's topic data using setDataSource action
          // The segmentId is the messagePath.id to identify which column to update
          dispatch(
            actions.setDataSource({
              topicData: messagePathNode.toTopicData(),
              panelId,
              segmentId: topicData.messagePath.id,
            }),
          );
        }}
        optionFilter={(messagePathNode) => {
          if (
            messagePathNode.data.message_path_id === topicData.messagePath.id
          ) {
            return true;
          }
          if (isExistingColumn(messagePathNode.toTopicData())) {
            return false;
          }
          return isViewableAsConsoleLog(messagePathNode);
        }}
      />
    </div>
  );
}
